import { memo, useEffect, useState } from 'react';

import SvgFillLibrary from './SvgFillLibrary';
import SvgFillManager from './SvgFillManager';
import { SvgFillManagerInterface } from '.';

/**
 * Managed SVG Fill Library
 * ========================
 *
 * This component connects the SvgFillManager singleton to the SvgFillLibrary component that renders
 * the gradients and other fills onto the page so they are accessible by SVG components.
 */

export default function createManagedSvgFillLibrary(
  svgFillManagerOrId?: SvgFillManager | string
): SvgFillManagerInterface {
  const svgFillManager =
    svgFillManagerOrId instanceof SvgFillManager
      ? svgFillManagerOrId
      : new SvgFillManager(svgFillManagerOrId);
  const ManagedSvgFillLibrary = () => {
    const [fills, setFills] = useState(svgFillManager.getFills());

    useEffect(() => {
      setFills(svgFillManager.getFills());

      const unsubscribe = svgFillManager.subscribe(() => {
        setFills(svgFillManager.getFills());
      });

      return () => {
        unsubscribe();
      };
    }, []);

    return <SvgFillLibrary fills={fills} />;
  };

  const getSvgFill: SvgFillManagerInterface['getSvgFill'] = (key) => svgFillManager.get(key);

  const registerSvgFill: SvgFillManagerInterface['registerSvgFill'] = (key, type, params) =>
    svgFillManager.get(svgFillManager.add(key, type, params))!;

  const registerSvgFills: SvgFillManagerInterface['registerSvgFills'] = (values) => {
    const svgPatterns: {
      [key: string]: string;
    } = {};
    const patternKeys = svgFillManager.addBatch(values);
    Object.entries(patternKeys).forEach(([key, patternKey]) => {
      svgPatterns[patternKey] = svgFillManager.get(patternKey)!; // sanitized
      svgPatterns[key] = svgFillManager.get(patternKey)!; // unsanitized
    });
    return svgPatterns;
  };

  const unregisterSvgFill: SvgFillManagerInterface['unregisterSvgFill'] = (key) =>
    svgFillManager.remove(key);

  return {
    ManagedSvgFillLibrary: memo(ManagedSvgFillLibrary),
    getSvgFill,
    registerSvgFill,
    registerSvgFills,
    unregisterSvgFill,
  };
}
